import { useAuthService } from '@/api/authService';
import { useAuthStore } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import { AppSwitcher } from '@teamworksdev/vuetify';
import { computed } from 'vue';
import { isEmpty, isNil } from 'lodash-es';
import { handleError } from '@/utils/errorHandling';
import { encodeTwrn } from '@/utils/twrn';

export function useAppSwitcher() {
	const authService = useAuthService();
	const authStore = useAuthStore();
	const userStore = useUserStore();

	async function getTokenFn() {
		try {
			const { token } = await authService.getOutgoingAxleToken();
			return token;
		} catch (e) {
			handleError(e);
			return '';
		}
	}

	const appSwitcher = computed(() => {
		if (!userStore.userAxleProfile) return null;
		return new AppSwitcher(
			AppSwitcher.slugs.INFLCR,
			getTokenFn,
			userStore.userAxleProfile.appTargetUrls,
			userStore.userAxleProfile.loginAccess['web'],
			!!authStore.impersonationToken,
		);
	});

	/**
	 * Deep links to an athlete's Hub profile via their TWRN. An optional `section`
	 * (e.g. `attributes?openedCategories=`, `forms`) targets a sub-page of the profile.
	 * See HUB-11661 / the "Deep Linking to Hub Profiles via TWRN" platform doc.
	 */
	async function redirectToHubProfile(twrn: string, section?: string): Promise<unknown | void> {
		const _appSwitcher = appSwitcher.value;

		if (isNil(_appSwitcher)) {
			return new Error('[useAppSwitcher] AppSwitcher not available');
		}

		if (isEmpty(twrn)) {
			return new Error('[useAppSwitcher] Athlete TWRN is required');
		}

		const encodedTwrn = encodeTwrn(twrn);

		if (!_appSwitcher.hasApp(AppSwitcher.slugs.HUB)) {
			return new Error('[useAppSwitcher] Hub not available for this profile');
		}

		const redirectPath = section
			? `/profiles/${encodedTwrn}/${section}`
			: `/profiles/${encodedTwrn}`;

		return _appSwitcher.switch(AppSwitcher.slugs.HUB, { redirect: redirectPath });
	}

	return {
		appSwitcher,
		redirectToHubProfile,
	};
}
